import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface ThresholdPreviewBarProps {
  currentValue: number | null | undefined;
  thresholdValue: number;
  operator: "BelowThreshold" | "AboveThreshold";
}

// Plain proportional-fill bar comparing the live Metabase value against the configured threshold -
// no charting library needed for a single-value comparison.
export const ThresholdPreviewBar = ({
  currentValue,
  thresholdValue,
  operator,
}: ThresholdPreviewBarProps) => {
  const { t } = useTranslation();

  if (currentValue === null || currentValue === undefined) {
    return (
      <div className="text-sm text-muted-foreground py-2">
        {t("noPreviewAvailable")}
      </div>
    );
  }

  const breached =
    operator === "BelowThreshold"
      ? currentValue < thresholdValue
      : currentValue > thresholdValue;

  const scaleMax = Math.max(currentValue, thresholdValue, 1) * 1.2;
  const currentPct = Math.min(100, (Math.abs(currentValue) / scaleMax) * 100);
  const thresholdPct = Math.min(
    100,
    (Math.abs(thresholdValue) / scaleMax) * 100,
  );

  return (
    <div className="py-2 space-y-1">
      <div className="flex justify-between text-sm">
        <span>
          {t("currentValue")}: <strong>{currentValue}</strong>
        </span>
        <span className={cn(breached ? "text-destructive" : "text-success")}>
          {breached ? t("wouldBreach") : t("withinRange")}
        </span>
      </div>
      <div className="relative h-3 rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            "h-full rounded-full transition-all",
            breached ? "bg-destructive" : "bg-success",
          )}
          style={{ width: `${currentPct}%` }}
        />
        <div
          className="absolute top-0 h-full w-0.5 bg-foreground"
          style={{ left: `${thresholdPct}%` }}
          title={`${t("threshold")}: ${thresholdValue}`}
        />
      </div>
    </div>
  );
};
